<script>
  /**
   * App shell navigation.
   *
   * Structure follows plugins/augurly's AppSidebar (collapsible, persisted,
   * mobile overlay), with two corrections:
   *
   *  1. Admin items are shown from `authStore.role` — an explicit server-stated
   *     value — rather than augurly's inferred `isClaimsAdmin` boolean.
   *  2. Active-link detection uses the router's reactive location. Augurly runs
   *     three redundant detectors including a 200ms setInterval polling
   *     window.location.pathname.
   */
  import { authStore } from '../stores/authStore.svelte.js';
  import { themeState, themeActions } from '$lib/theme.svelte.js';
  import api from '$lib/api.js';

  let { base = '/strao' } = $props();

  // Tracked from window.location rather than the router's `useRoute()`.
  //
  // The sidebar is part of the app shell and renders OUTSIDE <RouterView>.
  // `useRoute()` calls `useRouter()`, which needs the router context — and that
  // context does not reach here: <RouterContext>'s children snippet is
  // lexically owned by App.svelte, so components in it are instantiated with
  // App.svelte's context, not RouterContext's. RouterView papers over this for
  // route components by creating its own inner RouterContext when it finds
  // none; nothing does that for the shell. The result was
  // "Router context is missing" thrown on every authenticated page.
  //
  // The plan called for replacing the old popstate/click/setInterval trio with
  // the router's reactive route. That was right for anything inside the router
  // tree and wrong here. This is one listener, not three, and no polling.
  let currentPath = $state(pathFromLocation());

  function pathFromLocation() {
    const path = window.location.pathname;
    return path.startsWith(base) ? path.slice(base.length) || '/' : path;
  }

  $effect(() => {
    const sync = () => { currentPath = pathFromLocation(); };
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  });

  let collapsed = $state(readCollapsed());
  let mobileOpen = $state(false);

  function readCollapsed() {
    try { return localStorage.getItem('strao-portal-sidebar-collapsed') === 'true'; }
    catch { return false; }
  }

  function toggleCollapsed() {
    collapsed = !collapsed;
    try { localStorage.setItem('strao-portal-sidebar-collapsed', String(collapsed)); }
    catch { /* ignore */ }
  }

  const mainNav = [
    { label: 'Dashboard',     href: '/',              icon: 'fa-gauge',        exact: true },
    { label: 'Properties',    href: '/properties',    icon: 'fa-house' },
    { label: 'Conversations', href: '/conversations', icon: 'fa-comments' },
    { label: 'Reservations',  href: '/reservations',  icon: 'fa-calendar-days' },
  ];

  // Staff-only. Placed above the admin group because it is day-to-day work,
  // not administration.
  const staffNav = $derived(
    authStore.isStaff
      ? [
          { label: 'Review queue', href: '/review', icon: 'fa-list-check' },
          { label: 'Unlinked', href: '/unlinked', icon: 'fa-link-slash' },
          { label: 'Auto-responder', href: '/settings/tenant', icon: 'fa-sliders' },
          { label: 'Saved replies', href: '/situations', icon: 'fa-comment-dots' },
          { label: 'Rules for all properties', href: '/rules', icon: 'fa-scale-balanced' },
          { label: 'Pipeline health', href: '/pipeline', icon: 'fa-heart-pulse' },
        ]
      : []
  );

  const adminNav = $derived(
    authStore.canManageUsers
      ? [
          ...(authStore.canManageTenants
            ? [{ label: 'Tenants', href: '/admin/tenants', icon: 'fa-building' }]
            : []),
          { label: 'People & access', href: '/admin/users', icon: 'fa-user-shield' },
          { label: 'Audit log',       href: '/admin/audit', icon: 'fa-clipboard-list' },
          ...(authStore.isSuperUser
            ? [{ label: 'Test chat', href: '/admin/test-chat', icon: 'fa-flask' }]
            : []),
        ]
      : []
  );

  function isActive(item) {
    if (item.exact) return currentPath === item.href;
    return currentPath.startsWith(item.href);
  }

  function navigate(href) {
    mobileOpen = false;
    window.history.pushState({}, '', `${base}${href === '/' ? '/' : href}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async function switchTenant(event) {
    const tenantId = Number(event.currentTarget.value) || null;
    await authStore.loadContext(tenantId);
    window.dispatchEvent(new CustomEvent('strao:tenant-changed'));
  }

  async function logout() {
    await api.logout().catch(() => { /* best effort; local state is cleared regardless */ });
    authStore.clearAuth();
    navigate('/login');
  }
</script>

<button type="button" class="p-btn p-btn-quiet fixed left-3 top-3 z-40 md:hidden"
        aria-label="Open navigation" onclick={() => (mobileOpen = true)}>
  <i class="fas fa-bars" aria-hidden="true"></i>
</button>

{#if mobileOpen}
  <div class="fixed inset-0 z-40 bg-black/50 md:hidden" role="presentation"
       onclick={() => (mobileOpen = false)}></div>
{/if}

<aside class="z-40 flex shrink-0 flex-col border-r
              {mobileOpen ? 'fixed inset-y-0 left-0 w-64' : 'hidden md:flex'}
              {collapsed ? 'md:w-16' : 'md:w-64'} md:sticky md:top-0 md:h-screen"
       style="background-color: var(--p-bg-card); border-color: var(--p-border)">

  <div class="flex h-14 items-center gap-2 px-4" style="border-bottom: 1px solid var(--p-border)">
    <i class="fas fa-house-chimney" style="color: var(--p-brand)" aria-hidden="true"></i>
    {#if !collapsed}<span class="text-sm font-semibold">STRAO Portal</span>{/if}
  </div>

  {#if authStore.canSwitchTenants && !collapsed}
    <div class="px-3 py-3" style="border-bottom: 1px solid var(--p-border)">
      <label for="tenant-switch" class="text-xs font-medium p-muted">Viewing</label>
      <select id="tenant-switch" class="p-input mt-1 text-xs"
              value={authStore.tenantId ?? ''} onchange={switchTenant}>
        <option value="">All tenants</option>
        {#each authStore.availableTenants as tenant}
          <option value={tenant.id}>{tenant.name}{tenant.is_active === false ? ' (inactive)' : ''}</option>
        {/each}
      </select>
    </div>
  {/if}

  <nav class="flex-1 overflow-y-auto p-2">
    {#each mainNav as item}
      <button type="button" onclick={() => navigate(item.href)}
              class="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm"
              style={isActive(item)
                ? 'background-color: var(--p-brand-light); color: var(--p-brand)'
                : 'color: var(--p-text-muted)'}
              aria-current={isActive(item) ? 'page' : undefined}
              title={collapsed ? item.label : undefined}>
        <i class="fas {item.icon} w-4 text-center" aria-hidden="true"></i>
        {#if !collapsed}<span>{item.label}</span>{/if}
      </button>
    {/each}

    {#each staffNav as item}
      <button type="button" onclick={() => navigate(item.href)}
              class="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm"
              style={isActive(item)
                ? 'background-color: var(--p-brand-light); color: var(--p-brand)'
                : 'color: var(--p-text-muted)'}
              aria-current={isActive(item) ? 'page' : undefined}
              title={collapsed ? item.label : undefined}>
        <i class="fas {item.icon} w-4 text-center" aria-hidden="true"></i>
        {#if !collapsed}<span>{item.label}</span>{/if}
      </button>
    {/each}

    {#if adminNav.length}
      <p class="mt-4 px-3 text-xs font-semibold uppercase tracking-wide p-faint">
        {collapsed ? '·' : 'Administration'}
      </p>
      {#each adminNav as item}
        <button type="button" onclick={() => navigate(item.href)}
                class="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm"
                style={isActive(item)
                  ? 'background-color: var(--p-brand-light); color: var(--p-brand)'
                  : 'color: var(--p-text-muted)'}
                title={collapsed ? item.label : undefined}>
          <i class="fas {item.icon} w-4 text-center" aria-hidden="true"></i>
          {#if !collapsed}<span>{item.label}</span>{/if}
        </button>
      {/each}
    {/if}
  </nav>

  <div class="p-2" style="border-top: 1px solid var(--p-border)">
    <button type="button" onclick={() => navigate('/settings')}
            class="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm p-muted">
      <i class="fas fa-gear w-4 text-center" aria-hidden="true"></i>
      {#if !collapsed}<span>Settings</span>{/if}
    </button>

    <button type="button" onclick={themeActions.toggle}
            class="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm p-muted"
            aria-pressed={themeState.current === 'dark'}>
      <i class="fas {themeState.current === 'dark' ? 'fa-sun' : 'fa-moon'} w-4 text-center"
         aria-hidden="true"></i>
      {#if !collapsed}<span>{themeState.current === 'dark' ? 'Light mode' : 'Dark mode'}</span>{/if}
    </button>

    {#if !collapsed}
      <div class="px-3 py-2">
        <p class="truncate text-xs font-medium">{authStore.displayName}</p>
        <p class="truncate text-xs p-faint">{authStore.role ?? ''}</p>
      </div>
    {/if}

    <button type="button" onclick={logout}
            class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm p-muted">
      <i class="fas fa-arrow-right-from-bracket w-4 text-center" aria-hidden="true"></i>
      {#if !collapsed}<span>Sign out</span>{/if}
    </button>

    <button type="button" onclick={toggleCollapsed}
            class="hidden w-full items-center gap-3 rounded-lg px-3 py-2 text-sm p-faint md:flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
      <i class="fas {collapsed ? 'fa-angles-right' : 'fa-angles-left'} w-4 text-center"
         aria-hidden="true"></i>
      {#if !collapsed}<span>Collapse</span>{/if}
    </button>
  </div>
</aside>
